import { useEffect, useRef, useState } from 'react';
import { settingsService } from '../services/settingsService';

/**
 * Varebilde-konfigurasjon delt mellom plukk (36334101) og fritelling (TW 36391639).
 *
 * `items.imageBaseUrl` er master-gaten: tom/usatt = ingen bilder. `items.imageNameField`
 * peker på feltet som holder bildefilnavnet (default `ItemPictureName`; plukk støtter også
 * OrderLineFreeField1-10). Konfigurasjonen lastes én gang og ligger i en ref, så kallere
 * slipper settings som effect-dependency.
 */
export interface ItemImageConfig {
  baseUrl: string;
  field: string;
}

export function useItemImageConfig() {
  const imageConfigRef = useRef<ItemImageConfig | null>(null);
  // Re-render når konfigurasjonen er lastet — for kallere som bygger URL under rendering
  // (varekortet). Plukk/telling bygger URL ved skann og bruker bare ref-en.
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [baseUrlSetting, fieldSetting] = await Promise.all([
          settingsService.getEffectiveSetting('items.imageBaseUrl'),
          settingsService.getEffectiveSetting('items.imageNameField')
        ]);
        if (cancelled) return;
        const baseUrl = (baseUrlSetting?.currentValue || '').toString().trim();
        const field = (fieldSetting?.currentValue || '').toString().trim() || 'ItemPictureName';
        imageConfigRef.current = baseUrl ? { baseUrl: baseUrl.replace(/\/+$/, ''), field } : null;
      } catch (err) {
        console.warn('Could not load item image settings (items.imageBaseUrl/imageNameField):', err);
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Full URL til et varebilde ut fra filnavnet, eller undefined når funksjonen er av
   * (ingen base-URL), operatøren har slått bilder av (ShowItemPicureYesNo=false — null/true
   * betyr vis) eller navnet er tomt.
   */
  const buildItemImageUrl = (pictureName: string | null | undefined, showItemPicureYesNo?: boolean | null): string | undefined => {
    const cfg = imageConfigRef.current;
    if (!cfg) return undefined;
    if (showItemPicureYesNo === false) return undefined;
    const trimmed = pictureName?.trim();
    if (!trimmed) return undefined;
    return `${cfg.baseUrl}/${encodeURIComponent(trimmed)}`;
  };

  return { imageConfigRef, buildItemImageUrl, configLoaded };
}
